/**
 * Helper utilities for the Interactives microservice feature.
 * Loop-feed JSON API, RSS feeds, ad sizing, text formatting.
 */

/**
 * Fetches the loop-feed JSON API and normalises items into a common card shape:
 * { title, link, description, pubDate, image, author, authorImage, service }
 * @param {string} url
 * @returns {Promise<Array>}
 */
export const parseLoopFeed = async (url) => {
  if (!url) return [];
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const items = Array.isArray(json) ? json : [];

    // Returns true if a URL is a directly playable video file
    const isVideoUrl = (url) =>
      url && /\.(mp4|webm|m3u8|ogg|mov)(\?|$)/i.test(url);

    return items.map((item) => {
      const media = item.media || [];
      const service = (item.service || "").toLowerCase();
      const photoMedia = media.find((m) => m.type === "photo" || m.type === "image");

      // Only treat as video if the URL is a real playable file — not a thumbnail JPEG
      const videoMedia = media.find((m) => m.type === "video" && isVideoUrl(m.url));

      // For items whose video media entry has a non-playable URL (e.g. Twitter thumb),
      // use that URL as the image fallback instead.
      const thumbMedia = !videoMedia && media.find((m) => m.type === "video" && m.url);

      const image =
        photoMedia?.url ||
        videoMedia?.thumbnail ||
        thumbMedia?.url ||
        null;
      const video = videoMedia?.url || null;

      // Extract embed URL from the embed HTML string, e.g. <iframe src='...'></iframe>
      const embedHtml = media.find((m) => m.embed)?.embed || "";
      const embedMatch = embedHtml.match(/src=['"]([^'"]+)['"]/);
      const embedUrl = embedMatch ? embedMatch[1] : null;

      // Build native embed URLs for Twitter/Instagram posts so the FeedSlider
      // can render them as iframes (the API only gives us the post link, not a
      // playable video file).
      let socialEmbed = null;
      const postUrl = item.url || "";
      if (service === "twitter" || service === "x") {
        // https://twitter.com/user/status/1234  OR  https://x.com/user/status/1234
        const tweetIdMatch = postUrl.match(/\/status\/(\d+)/);
        if (tweetIdMatch) {
          socialEmbed = `https://platform.twitter.com/embed/Tweet.html?id=${tweetIdMatch[1]}&theme=light&autoplay=1&muted=true`;
        }
      } else if (service === "instagram") {
        // https://www.instagram.com/p/ABC123/  OR  /reel/ABC123/
        const igMatch = postUrl.match(/\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
        if (igMatch) {
          socialEmbed = `https://www.instagram.com/p/${igMatch[2]}/embed/?autoplay=true&muted=true`;
        }
      }

      return {
        title: item.title || sanitizeHtml(item.text) || "",
        link: postUrl,
        description: sanitizeHtml(item.text) || "",
        pubDate: item.date || item.created || "",
        image,
        video,
        embedUrl,
        socialEmbed,
        author: item.author?.name || "",
        authorImage: item.author?.image || null,
        service,
      };
    });
  } catch (err) {
    console.error("[interactivesHelper] parseLoopFeed error:", err);
    return [];
  }
};

/**
 * Fetches and parses an RSS feed URL into an array of item objects.
 * @param {string} url - RSS feed URL
 * @returns {Promise<Array>}
 */
export const parseRssFeed = async (url) => {
  if (!url) return [];
  try {
    const res = await fetch(url);
    const text = await res.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, "text/xml");
    const items = Array.from(xml.querySelectorAll("item"));
    return items.map((item) => {
      const get = (tag) => item.querySelector(tag)?.textContent?.trim() || "";
      const enclosure = item.querySelector("enclosure");
      const mediaThumbnail =
        item.getElementsByTagNameNS("http://search.yahoo.com/mrss/", "thumbnail")[0] ||
        item.getElementsByTagNameNS("http://search.yahoo.com/mrss/", "content")[0];

      return {
        title: get("title"),
        link: get("link"),
        description: sanitizeHtml(get("description")),
        pubDate: get("pubDate"),
        author: get("author") || get("dc:creator"),
        image:
          enclosure?.getAttribute("url") ||
          mediaThumbnail?.getAttribute("url") ||
          null,
      };
    });
  } catch (err) {
    console.error("[interactivesHelper] parseRssFeed error:", err);
    return [];
  }
};

/**
 * Strips HTML tags from a string for safe plain-text rendering.
 * @param {string} str
 * @returns {string}
 */
export const sanitizeHtml = (str) => {
  if (!str) return "";
  return str.replace(/<[^>]*>/g, "").replace(/&[a-z]+;/gi, " ").trim();
};

/**
 * Truncates text to maxLen characters and appends ellipsis if needed.
 * @param {string} str
 * @param {number} maxLen
 * @returns {string}
 */
export const truncateText = (str, maxLen = 120) => {
  if (!str) return "";
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen).trimEnd() + "…";
};

/**
 * Formats an RSS pubDate string into a human-readable date.
 * @param {string} dateStr - e.g. "Mon, 17 Feb 2026 10:00:00 +0000"
 * @returns {string} e.g. "17 Feb 2026"
 */
export const formatRssDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

/**
 * Converts a ratio string like "300x250" into a width/height object.
 * @param {string} ratio - e.g. "300x250", "728x90"
 * @returns {{ width: number, height: number }}
 */
export const buildAdSize = (ratio = "300x250") => {
  const parts = ratio.toLowerCase().split("x");
  const width = parseInt(parts[0], 10) || 300;
  const height = parseInt(parts[1], 10) || 250;
  return { width, height };
};

/**
 * Reads a data attribute from a .microservice-cont DOM element.
 * @param {string} attr - attribute name without "data-" prefix, e.g. "feedurl"
 * @param {HTMLElement} [containerEl] - specific container element (for multi-widget pages)
 * @returns {string|null}
 */
export const getInteractivesDataAttr = (attr, containerEl) => {
  try {
    const el = containerEl || document.querySelector(".microservice-cont");
    return el?.getAttribute(`data-${attr}`) || null;
  } catch {
    return null;
  }
};
